import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import octopusInstance from "../../services/octopus/octopusInstance";
import { fetchOptionChainAction } from "../../store/actions/watchlist";
import OptionsHeader from "./OptionsHeader";
import Box from "@mui/material/Box";
import CallPutInstruments from "./CallPutInstruments";
import Paper from "@mui/material/Paper";
import { getExchangeCode, getStrike } from "../../services/exchanges";
import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlined';
import { useLocation } from 'react-router-dom';
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";



const StyledCheck = styled('div')(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    right: '2%',
    marginTop: 0,
  },
  '@media (max-width: 765px)': {
    marginTop: 0,
  },
}));

function OptionChainOverview({ exchange, token, scripInfo }) {
  console.log("scripInfo", scripInfo);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(null);
  let optionChainData = useSelector(state => state.marketwatch.optionChainData);

  const [num, setNum] = React.useState(18);
  const [optionChain, setOptionChain] = useState([]);
  const [chartQuery, setChartQuery] = useState({});
  const [switchViews, setSwitchViews] = useState(false);
  const [underlyingInstrumentData, setUnderlyingInstrumentData] = useState({});
  const [oneTimeFetchLTP, setOneTimeFetchLTP] = useState(false);

  // };

  const getLTP = () => {
    if (scripInfo.instrument_name?.startsWith("OPT")) {
      return exchange !== "MCX" ? getStrike(exchange, Number(scripInfo.strike)) : Number(scripInfo.strike / 100);
    } else {
      return underlyingInstrumentData.ltp;
    }
  };

  useEffect(() => {
    if (optionChainData.length) {
      setOptionChain(optionChainData);
      // Set the first expiry date as selected when data loads
      if (!selectedExpiryDate && optionChainData[0]?.expiry_date) {
        setSelectedExpiryDate(optionChainData[0].expiry_date);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [optionChainData]);

  useEffect(() => {
    setUnderlyingInstrumentData({});
    setNum(18);
    setOneTimeFetchLTP(false);
    optionChainData.length && setOptionChain([]);
    setLoading(false);

  }, [exchange, token]);

  useEffect(() => {
    if (scripInfo?.underlying_token || exchange === "BSE" && scripInfo?.is_index) {
      const subscriptionLocation = `optchain-undLying-NSE-${scripInfo?.underlying_token}`;
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation,
        payload: {
          exchangeCode: exchange === "CDS" ? 3 : exchange === "MCX" ? 4 :
            exchange === "BSE" && scripInfo?.is_index || exchange === "NSE" && scripInfo?.is_index ?
              getExchangeCode(exchange) : 1,
          instrumentToken: exchange === "CDS" || exchange === "MCX" ?
            scripInfo?.instrument_token :
            scripInfo?.is_index ? scripInfo?.instrument_token : scripInfo?.underlying_token,
        },
      });

      handler.subscribe(({ msg }) => {
        setUnderlyingInstrumentData(msg);
        if (!oneTimeFetchLTP && msg.ltp !== 0) {
          setOneTimeFetchLTP(true);
        }
      });

      return handler.unsubscribe;
    }
  }, [scripInfo, num]);

  useEffect(() => {
    optionChainData.length && setOptionChain([]);
    setLoading(true);

    if (oneTimeFetchLTP) {

      const requestPayload = {
        exchange: scripInfo?.is_index && exchange === "BSE" ? "BFO" :
          scripInfo?.is_index && exchange === "NSE" ? "NSE" : exchange,
        token: exchange === "MCX" && scripInfo?.instrument_name?.startsWith("FUT") ?
          scripInfo?.instrument_token :
          exchange === "BSE" && scripInfo?.is_index ?
            scripInfo?.instrument_token : scripInfo?.underlying_token,
        num,
        price: getLTP(),
        instrument_name: scripInfo?.instrument_name
      };
      dispatch(fetchOptionChainAction(requestPayload))
        .catch(error => {
          setLoading(false);
        });
    }


    setUnderlyingInstrumentData({});
  }, [oneTimeFetchLTP, num]);

  const getUnderlyingToken = () => {
    if (scripInfo.instrument_name === "FUTCOM") {
      return scripInfo.instrument_token;
    } else if (scripInfo.instrument_name === "FUTSTK" || "FUTIDX") {
      return scripInfo.underlying_token;
    }
    else {
      return scripInfo?.fut_underlying_token || scripInfo?.underlying_token;
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        width: "100%",
        height: "100%",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            flex: '0 0 auto'
          }}
        >
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "background.paper"
            }}
            component={StyledCheck}
          >
            {optionChain.length > 0 ? (
              <OptionsHeader
                display_name={scripInfo.display_name}
                trading_symbol={scripInfo.trading_symbol}
                exchange={exchange}
                token={scripInfo?.instrument_token}
                num={num}
                setNum={setNum}
                isIndex={scripInfo?.is_index}
                setSwitchViews={setSwitchViews}
                setChartQuery={setChartQuery}
                instrument={scripInfo}
                expiryDates={optionChain.map(item => item.expiry_date)}
                selectedExpiryDate={selectedExpiryDate}
                setSelectedExpiryDate={setSelectedExpiryDate}
              />
            ) : null}
          </Box>
        </Box>

        <Box sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          p: { xs: 1, sm: 2 },
          touchAction: 'pan-y',
        }}>
          {loading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              textAlign: 'center'
            }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main
                }}
              />
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}
              >
                Loading Option Chain...
              </Typography>
            </Box>
          ) : optionChain.length > 0 ? (
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: 1,
              scrollbarWidth: 'thin',
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '3px'
              }
            }}>
              {optionChain
                .filter(row => row.expiry_date === selectedExpiryDate)
                .map((row, index) => (
                  <CallPutInstruments
                    key={index}
                    row={row}
                    instrument={scripInfo}
                    panel={`panel${index}`}
                    setChartQuery={setChartQuery}
                    setSwitchViews={setSwitchViews}
                    exchange={exchange}
                    token={token}
                    underlyingToken={getUnderlyingToken()}
                    underlineTradingSymbol={scripInfo?.trading_symbol}
                  />
                ))}
              <Box sx={{ height: '30px' }} />
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              textAlign: 'center'
            }}>
              <InsertChartOutlinedTwoToneIcon
                color="disabled"
                sx={{
                  fontSize: "4rem",
                  opacity: 0.5
                }}
              />
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}
              >
                Option Chain Not Available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper >
  );
}

export default OptionChainOverview; 